import React, { useEffect, useMemo, useState } from "react";
import {
  Home, Users, CreditCard, Activity, Trophy, Cpu, ClipboardList,
  Search, RefreshCw, ShieldAlert, Crown, Coins, Sparkles, Gift, RotateCcw,
  Trash2, ArrowLeft, ExternalLink, CheckCircle2, Clock, LogIn,
} from "lucide-react";
import {
  adminWhoami, adminStats, adminSearchPlayers, adminGetPlayer,
  adminGrantVip, adminRemoveVip, adminGrantFounder, adminRemoveFounder,
  adminGrantCurrency, adminResetCrate, adminUnlockWorlds,
  adminResetProgress, adminResetCosmetics, adminResetAchievements,
  adminPurchases, adminActivity, adminLeaderboard, adminLeaderboardDelete,
  adminSystem, adminAudit,
} from "../game/adminService";
import { startGoogleLogin, fetchMe, logout } from "../game/authService";

const TABS = [
  { id: "home", label: "Home", Icon: Home },
  { id: "players", label: "Players", Icon: Users },
  { id: "purchases", label: "Purchases", Icon: CreditCard },
  { id: "activity", label: "Activity", Icon: Activity },
  { id: "leaderboard", label: "Leaderboard", Icon: Trophy },
  { id: "system", label: "System", Icon: Cpu },
  { id: "audit", label: "Audit", Icon: ClipboardList },
];

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch { return s; }
}

function fmtMoney(n) {
  if (typeof n !== "number") return "$0.00";
  return `$${n.toFixed(2)}`;
}

// ---------- HOME ----------
function HomeTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    setErr("");
    try { setData(await adminStats()); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to load stats"); }
  };
  useEffect(() => { load(); }, []);
  if (err) return <div className="admin-err">{err}</div>;
  if (!data) return <div className="admin-loading">Loading…</div>;
  return (
    <div className="admin-home">
      <div className="admin-stat-grid">
        <StatCard label="Total Players" value={data.total_players} />
        <StatCard label="New Today" value={data.new_today} accent="green" />
        <StatCard label="Active Today" value={data.active_today} accent="green" />
        <StatCard label="VIP Active" value={data.vip_active} accent="purple" />
        <StatCard label="Founders" value={data.founder_count} accent="purple" />
        <StatCard label="Total Runs" value={data.total_runs.toLocaleString()} />
        <StatCard label="Total Zombies" value={data.total_zombies.toLocaleString()} />
        <StatCard label="Bosses Defeated" value={data.total_bosses.toLocaleString()} />
        <StatCard label="Revenue" value={fmtMoney(data.total_revenue)} accent="green" />
        <StatCard label="Revenue Today" value={fmtMoney(data.revenue_today)} accent="green" />
        <StatCard label="Purchases" value={data.total_purchases} />
        <StatCard label="Purchases Today" value={data.purchases_today} />
      </div>
      <div className="admin-row-2">
        <div className="admin-card">
          <h3>Recent Signups</h3>
          {data.recent_signups.length === 0 ? <p className="muted">No signups yet.</p> : (
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Joined</th></tr></thead>
              <tbody>
                {data.recent_signups.map((u) => (
                  <tr key={u.user_id}>
                    <td>{u.name || "—"}</td>
                    <td className="mono">{u.email}</td>
                    <td className="muted">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="admin-card">
          <h3>Recent Purchases</h3>
          {data.recent_purchases.length === 0 ? <p className="muted">No purchases yet.</p> : (
            <table className="admin-table">
              <thead><tr><th>Plan</th><th>Email</th><th>Amount</th><th>When</th></tr></thead>
              <tbody>
                {data.recent_purchases.map((p) => (
                  <tr key={p.session_id}>
                    <td><span className="pill purple">{p.plan_id}</span></td>
                    <td className="mono">{p.email || "—"}</td>
                    <td className="mono">{fmtMoney(p.amount)}</td>
                    <td className="muted">{fmtDate(p.applied_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="admin-card admin-health">
        <h3><CheckCircle2 size={16} /> Health</h3>
        <div className="admin-health-row">
          <span>DB: <b className={data.health.db === "ok" ? "ok" : "bad"}>{data.health.db}</b></span>
          <span>Version: <b>{data.health.version}</b></span>
          <span>Stripe: <b>{data.health.stripe_mode}</b></span>
          <span className="muted">{fmtDate(data.health.time)}</span>
          <button className="admin-btn refresh" onClick={load}><RefreshCw size={12} /> Refresh</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`admin-stat-card ${accent ? `acc-${accent}` : ""}`}>
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
    </div>
  );
}

// ---------- PLAYERS ----------
function PlayersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");

  const search = async () => {
    setErr("");
    setLoading(true);
    try { setRows((await adminSearchPlayers(q)).players); }
    catch (e) { setErr(e?.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { search(); /* initial empty load */ }, []);

  if (selected) {
    return <PlayerProfile userId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="admin-card">
      <div className="admin-search-row">
        <Search size={16} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, name, or user ID…"
          onKeyDown={(e) => e.key === "Enter" && search()}
          data-testid="admin-player-search-input"
        />
        <button className="admin-btn" onClick={search} disabled={loading} data-testid="admin-player-search-btn">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>
      {err && <div className="admin-err">{err}</div>}
      <table className="admin-table">
        <thead><tr><th>Name</th><th>Email</th><th>Joined</th><th>Lv</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.user_id}>
              <td>{u.name || "—"}</td>
              <td className="mono">{u.email}</td>
              <td className="muted">{fmtDate(u.created_at)}</td>
              <td>{u.level}</td>
              <td>
                {u.founder ? <span className="pill purple">Founder</span> :
                  u.is_vip ? <span className="pill purple">VIP</span> :
                    <span className="pill muted">Free</span>}
              </td>
              <td>
                <button
                  className="admin-btn small"
                  onClick={() => setSelected(u.user_id)}
                  data-testid={`admin-open-player-${u.user_id}`}
                >Open</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && !loading && (
            <tr><td colSpan={6} className="muted center">No players found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ConfirmModal({ title, onConfirm, onCancel, body }) {
  const [val, setVal] = useState("");
  return (
    <div className="admin-modal-bg" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3><ShieldAlert size={18} /> {title}</h3>
        <p>{body}</p>
        <p className="muted small">Type <code>RESET</code> below to confirm:</p>
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="RESET"
          autoFocus
          data-testid="admin-confirm-input"
        />
        <div className="admin-modal-buttons">
          <button className="admin-btn" onClick={onCancel} data-testid="admin-confirm-cancel">Cancel</button>
          <button
            className="admin-btn danger"
            disabled={val !== "RESET"}
            onClick={() => onConfirm()}
            data-testid="admin-confirm-go"
          >
            <Trash2 size={14} /> Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayerProfile({ userId, onBack }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [vipDays, setVipDays] = useState(7);
  const [currencyKind, setCurrencyKind] = useState("coins");
  const [currencyAmt, setCurrencyAmt] = useState(100);

  const load = async () => {
    setErr("");
    try { setP(await adminGetPlayer(userId)); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, [userId]);

  const wrap = (fn, success) => async (...args) => {
    setMsg("");
    try {
      await fn(...args);
      setMsg(success);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed");
    }
  };

  if (err) return <div className="admin-err">{err} <button className="admin-btn" onClick={onBack}>Back</button></div>;
  if (!p) return <div className="admin-loading">Loading player…</div>;

  return (
    <div className="admin-card">
      <div className="admin-prof-head">
        <button className="admin-btn" onClick={onBack} data-testid="admin-prof-back"><ArrowLeft size={14} /> Back</button>
        <div className="admin-prof-name">
          {p.picture && <img src={p.picture} alt="" className="admin-prof-pic" />}
          <div>
            <h2 className="admin-prof-title">{p.name || "—"}</h2>
            <div className="muted mono">{p.email}</div>
            <div className="muted small mono">id: {p.user_id}</div>
          </div>
        </div>
      </div>

      {msg && <div className="admin-ok" data-testid="admin-action-msg">{msg}</div>}

      <div className="admin-row-2">
        <div className="admin-sub">
          <h4>Account</h4>
          <Field k="Joined" v={fmtDate(p.raw_created_at)} />
          <Field k="Last login" v={fmtDate(p.raw_last_login)} />
          <Field k="Level" v={p.level} />
          <Field k="XP" v={(p.xp || 0).toLocaleString()} />
          <Field k="Talent Points" v={p.talent_points} />
          <Field k="VIP" v={p.founder ? "Founder" : p.is_vip ? p.vip_plan || "active" : "no"} />
          <Field k="VIP until" v={fmtDate(p.vip_until)} />
          <Field k="Daily crate" v={p.daily_crate_available ? "available" : "claimed today"} />
        </div>
        <div className="admin-sub">
          <h4>Currencies</h4>
          <Field k="Coins" v={p.coins} />
          <Field k="Gems" v={p.gems} />
          <Field k="Cosmetic Tokens" v={p.cosmetic_tokens} />
          <Field k="Reroll Tokens" v={p.reroll_tokens} />
        </div>
        <div className="admin-sub">
          <h4>Lifetime stats</h4>
          <Field k="Total runs" v={(p.stats || {}).total_games || 0} />
          <Field k="Total zombies" v={(p.stats || {}).total_zombies || 0} />
          <Field k="Highest wave" v={(p.stats || {}).highest_wave || 1} />
          <Field k="Bosses defeated" v={(p.stats || {}).bosses_defeated || 0} />
          <Field k="Worlds cleared" v={(p.owned_cosmetics || []).length === 0 ? "—" : "see equipped"} />
          <Field k="Achievements unlocked" v={Object.values(p.achievements || {}).filter((a) => a?.unlocked).length} />
          <Field k="Achievements claimed" v={Object.values(p.achievements || {}).filter((a) => a?.claimed).length} />
          <Field k="Cosmetics owned" v={(p.owned_cosmetics || []).length} />
          <Field k="Equipped" v={Object.keys(p.equipped || {}).length} />
        </div>
      </div>

      <h4>Support actions</h4>
      <div className="admin-actions">
        <div className="admin-action-line">
          <label className="muted small">Grant VIP days:</label>
          <input type="number" min={1} max={365} value={vipDays} onChange={(e) => setVipDays(parseInt(e.target.value || 0, 10))} data-testid="admin-vip-days" />
          <button className="admin-btn" onClick={wrap(() => adminGrantVip(userId, vipDays), `+${vipDays} VIP days granted`)} data-testid="admin-grant-vip"><Crown size={14} /> Grant VIP</button>
          <button className="admin-btn" onClick={wrap(() => adminRemoveVip(userId), "VIP removed")} data-testid="admin-remove-vip">Remove VIP</button>
        </div>
        <div className="admin-action-line">
          <button className="admin-btn" onClick={wrap(() => adminGrantFounder(userId), "Founder granted")} data-testid="admin-grant-founder"><Crown size={14} /> Grant Founder</button>
          <button className="admin-btn" onClick={wrap(() => adminRemoveFounder(userId), "Founder removed")} data-testid="admin-remove-founder">Remove Founder</button>
        </div>
        <div className="admin-action-line">
          <label className="muted small">Add currency:</label>
          <select value={currencyKind} onChange={(e) => setCurrencyKind(e.target.value)} data-testid="admin-currency-kind">
            <option value="coins">Coins</option>
            <option value="gems">Gems</option>
            <option value="cosmetic_tokens">Cosmetic Tokens</option>
            <option value="reroll_tokens">Reroll Tokens</option>
            <option value="talent_points">Talent Points</option>
          </select>
          <input type="number" value={currencyAmt} onChange={(e) => setCurrencyAmt(parseInt(e.target.value || 0, 10))} data-testid="admin-currency-amt" />
          <button className="admin-btn" onClick={wrap(() => adminGrantCurrency(userId, currencyKind, currencyAmt), `Added ${currencyAmt} ${currencyKind}`)} data-testid="admin-grant-currency"><Coins size={14} /> Add</button>
        </div>
        <div className="admin-action-line">
          <button className="admin-btn" onClick={wrap(() => adminResetCrate(userId), "Daily crate reset")} data-testid="admin-reset-crate"><Gift size={14} /> Reset Daily Crate</button>
          <button className="admin-btn" onClick={wrap(() => adminUnlockWorlds(userId), "World-unlock intent logged. Player must clear localStorage on their device.")} data-testid="admin-unlock-worlds"><Sparkles size={14} /> Unlock Worlds (intent)</button>
        </div>
        <div className="admin-action-line danger">
          <span className="muted small">Destructive (typed-confirm):</span>
          <button className="admin-btn danger" onClick={() => setConfirm("progress")} data-testid="admin-reset-progress"><RotateCcw size={14} /> Reset Progress</button>
          <button className="admin-btn danger" onClick={() => setConfirm("cosmetics")} data-testid="admin-reset-cosmetics"><RotateCcw size={14} /> Reset Cosmetics</button>
          <button className="admin-btn danger" onClick={() => setConfirm("achievements")} data-testid="admin-reset-achievements"><RotateCcw size={14} /> Reset Achievements</button>
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          title={`Reset ${confirm}?`}
          body={`This will wipe ${confirm} for ${p.email}. This cannot be undone.`}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            try {
              if (confirm === "progress") await adminResetProgress(userId);
              if (confirm === "cosmetics") await adminResetCosmetics(userId);
              if (confirm === "achievements") await adminResetAchievements(userId);
              setMsg(`${confirm} reset`);
              setConfirm(null);
              await load();
            } catch (e) {
              setErr(e?.response?.data?.detail || "Reset failed");
              setConfirm(null);
            }
          }}
        />
      )}
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="admin-field">
      <span className="admin-field-k">{k}</span>
      <span className="admin-field-v">{v}</span>
    </div>
  );
}

// ---------- PURCHASES ----------
function PurchasesTab() {
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const load = async () => {
    setErr("");
    try {
      const d = await adminPurchases({ plan: plan || undefined, status: status || undefined, q: q || undefined });
      setRows(d.purchases);
    } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plan, status]);
  return (
    <div className="admin-card">
      <div className="admin-filter-row">
        <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="admin-filter-plan">
          <option value="">All plans</option>
          <option value="vip_weekly">Weekly VIP</option>
          <option value="vip_monthly">Monthly VIP</option>
          <option value="vip_lifetime">Lifetime VIP</option>
          <option value="founder">Founder Pack</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status">
          <option value="">All status</option>
          <option value="paid">Successful</option>
          <option value="unpaid">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email contains…" onKeyDown={(e) => e.key === "Enter" && load()} data-testid="admin-filter-q" />
        <button className="admin-btn" onClick={load} data-testid="admin-filter-go">Apply</button>
      </div>
      {err && <div className="admin-err">{err}</div>}
      <table className="admin-table" data-testid="admin-purchases-table">
        <thead><tr><th>When</th><th>Plan</th><th>Email</th><th>Amount</th><th>Status</th><th>Applied</th><th>Session</th></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.session_id}>
              <td className="muted">{fmtDate(p.applied_at || p.created_at)}</td>
              <td><span className="pill purple">{p.plan_id}</span></td>
              <td className="mono">{p.email || "—"}</td>
              <td className="mono">{fmtMoney(p.amount)}</td>
              <td>{p.payment_status || "—"}</td>
              <td>{p.applied ? <span className="ok">✓</span> : <span className="bad">✗</span>}</td>
              <td className="mono small">{(p.session_id || "").slice(0, 24)}…</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="muted center">No purchases.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- ACTIVITY ----------
function ActivityTab() {
  const [data, setData] = useState(null);
  useEffect(() => { adminActivity(30).then(setData).catch(() => setData({ recent_logins: [], recent_runs: [], recent_purchases: [] })); }, []);
  if (!data) return <div className="admin-loading">Loading…</div>;
  return (
    <div className="admin-row-2">
      <div className="admin-card">
        <h3><LogIn size={14} /> Recent logins</h3>
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Email</th><th>When</th></tr></thead>
          <tbody>
            {data.recent_logins.map((u) => (
              <tr key={u.user_id}><td>{u.name || "—"}</td><td className="mono">{u.email}</td><td className="muted">{fmtDate(u.last_login)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-card">
        <h3><Activity size={14} /> Recent runs</h3>
        <table className="admin-table">
          <thead><tr><th>Player</th><th>Wave</th><th>Score</th><th>When</th></tr></thead>
          <tbody>
            {data.recent_runs.map((r) => (
              <tr key={r.id}>
                <td>{r.name || "—"} {r.is_founder ? <span className="pill purple">F</span> : r.is_vip ? <span className="pill purple">VIP</span> : null}</td>
                <td>{r.wave}</td><td>{r.score}</td><td className="muted">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-card admin-span-2">
        <h3><CreditCard size={14} /> Recent purchases</h3>
        <table className="admin-table">
          <thead><tr><th>When</th><th>Plan</th><th>Email</th><th>Amount</th></tr></thead>
          <tbody>
            {data.recent_purchases.map((p) => (
              <tr key={p.session_id}><td className="muted">{fmtDate(p.applied_at)}</td><td>{p.plan_id}</td><td className="mono">{p.email}</td><td>{fmtMoney(p.amount)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- LEADERBOARD ----------
function LeaderboardTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const load = async () => { try { setRows((await adminLeaderboard(q)).entries); } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); } };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this leaderboard entry?")) return;
    try { await adminLeaderboardDelete(id); load(); } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="admin-card">
      <div className="admin-search-row">
        <Search size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player name…" onKeyDown={(e) => e.key === "Enter" && load()} data-testid="admin-lb-q" />
        <button className="admin-btn" onClick={load} data-testid="admin-lb-search">Search</button>
      </div>
      {err && <div className="admin-err">{err}</div>}
      <table className="admin-table">
        <thead><tr><th>#</th><th>Player</th><th>Wave</th><th>Score</th><th>Tag</th><th>When</th><th></th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td className="muted">{i + 1}</td>
              <td>{r.name || "anonymous"}</td>
              <td>{r.wave}</td>
              <td>{r.score}</td>
              <td>{r.is_founder ? "Founder" : r.is_vip ? "VIP" : "—"}</td>
              <td className="muted">{fmtDate(r.created_at)}</td>
              <td><button className="admin-btn danger small" onClick={() => remove(r.id)} data-testid={`admin-lb-del-${r.id}`}><Trash2 size={12} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- SYSTEM ----------
function SystemTab() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => { try { setS(await adminSystem()); } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); } };
  useEffect(() => { load(); }, []);
  if (err) return <div className="admin-err">{err}</div>;
  if (!s) return <div className="admin-loading">Loading…</div>;
  return (
    <div className="admin-card">
      <h3><Cpu size={14} /> System</h3>
      <Field k="DB" v={<span className={s.db === "ok" ? "ok" : "bad"}>{s.db}</span>} />
      <Field k="Version" v={s.version} />
      <Field k="Environment" v={s.environment} />
      <Field k="Stripe mode" v={s.stripe_mode} />
      <Field k="Google Auth" v={s.google_auth} />
      <Field k="Admin emails configured" v={s.admin_emails_count} />
      <Field k="Server time" v={fmtDate(s.time)} />
      <button className="admin-btn" onClick={load}><RefreshCw size={12} /> Refresh</button>
    </div>
  );
}

// ---------- AUDIT ----------
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const load = async () => { try { setRows((await adminAudit(200)).entries); } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); } };
  useEffect(() => { load(); }, []);
  return (
    <div className="admin-card">
      <h3><ClipboardList size={14} /> Admin Audit Log</h3>
      {err && <div className="admin-err">{err}</div>}
      <table className="admin-table">
        <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Target</th><th>Old → New</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="muted">{fmtDate(r.ts)}</td>
              <td className="mono">{r.admin_email}</td>
              <td><span className="pill">{r.action}</span></td>
              <td className="mono small">{r.target_email || r.target_user_id || "—"}</td>
              <td className="mono small muted">{r.old ? JSON.stringify(r.old).slice(0, 80) : ""}{r.old && r.new ? " → " : ""}{r.new ? JSON.stringify(r.new).slice(0, 80) : ""}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="muted center">No audit entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------- ROOT ----------
export default function AdminScreen() {
  const [tab, setTab] = useState("home");
  const [me, setMe] = useState(null);
  const [gateErr, setGateErr] = useState("");
  const [signedInAs, setSignedInAs] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Override the game's global `body { overflow: hidden }` so the admin
    // dashboard can scroll. Cleanup restores normal behavior on unmount.
    document.body.classList.add("admin-mode");
    return () => document.body.classList.remove("admin-mode");
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminWhoami();
        if (!cancelled) setMe(res);
      } catch (e) {
        const status = e?.response?.status;
        // For 403, the backend echoes back signed_in_as so the owner can see
        // which Google account they're using.
        const detail = e?.response?.data?.detail;
        const echoedEmail =
          typeof detail === "object" && detail !== null ? detail.signed_in_as : null;
        if (cancelled) return;
        if (status === 403) {
          setSignedInAs(echoedEmail || null);
          setGateErr("not_admin");
        } else {
          // 401 — still try to read the public /api/auth/me in case there IS
          // a session but cookie scoping prevented the admin endpoint from
          // seeing it.
          try {
            const m = await fetchMe();
            if (!cancelled && m && m.email) setSignedInAs(m.email);
          } catch (_e) {
            /* no session — fall through to the not_signed_in gate */
          }
          if (!cancelled) setGateErr("not_signed_in");
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSwitchAccount = async () => {
    // Force a fresh OAuth round-trip. Logging out clears our session cookie
    // so the next Emergent OAuth flow will let the user pick a different
    // Google account (e.g. switch from Workspace to personal Gmail).
    try { await logout(); } catch (_e) { /* logout error is non-fatal */ }
    startGoogleLogin("/admin");
  };

  const TabBody = useMemo(() => {
    switch (tab) {
      case "players": return PlayersTab;
      case "purchases": return PurchasesTab;
      case "activity": return ActivityTab;
      case "leaderboard": return LeaderboardTab;
      case "system": return SystemTab;
      case "audit": return AuditTab;
      default: return HomeTab;
    }
  }, [tab]);

  if (checking) return <div className="admin-shell"><div className="admin-loading">Checking access…</div></div>;

  if (gateErr === "not_signed_in") {
    return (
      <div className="admin-shell" data-testid="admin-gate-signin">
        <div className="admin-gate">
          <ShieldAlert size={36} />
          <h1>Owner Sign-in Required</h1>
          {signedInAs ? (
            <p>
              You appear to be signed in as <code className="mono">{signedInAs}</code>,
              but the admin endpoint can&apos;t read your session. Try the
              switch-account button below.
            </p>
          ) : (
            <p>Please sign in with the owner Google account to access this page.</p>
          )}
          <div className="admin-gate-buttons">
            <button className="admin-btn primary" onClick={() => startGoogleLogin("/admin")} data-testid="admin-gate-signin-btn">
              <LogIn size={14} /> Sign in with Google
            </button>
            <button className="admin-btn" onClick={handleSwitchAccount} data-testid="admin-gate-switch-btn">
              Switch Google account
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (gateErr === "not_admin") {
    return (
      <div className="admin-shell" data-testid="admin-gate-forbidden">
        <div className="admin-gate">
          <ShieldAlert size={36} />
          <h1>Not authorized</h1>
          {signedInAs ? (
            <>
              <p>
                You&apos;re signed in as <code className="mono" data-testid="admin-gate-signed-in-as">{signedInAs}</code>.
              </p>
              <p className="muted small">
                This account is not on the admin allow-list. If you have multiple
                Google accounts (e.g. a Workspace and a personal one), use
                &quot;Switch Google account&quot; below to pick the right one.
                Otherwise ask the owner to whitelist this email or its domain.
              </p>
            </>
          ) : (
            <p>Your account doesn&apos;t have admin access.</p>
          )}
          <div className="admin-gate-buttons">
            <button className="admin-btn primary" onClick={handleSwitchAccount} data-testid="admin-gate-switch-btn">
              <LogIn size={14} /> Switch Google account
            </button>
            <a href="/" className="admin-btn">Back to game</a>
          </div>
        </div>
      </div>
    );
  }

  const Tab = TabBody;

  return (
    <div className="admin-shell" data-testid="admin-shell">
      <header className="admin-top">
        <div className="admin-brand"><ShieldAlert size={18} /> ZC3D Admin</div>
        <div className="admin-who">
          {me?.email && <span className="mono small muted">{me.email}</span>}
          <a className="admin-btn small" href="/" data-testid="admin-back-to-game"><ExternalLink size={12} /> Game</a>
        </div>
      </header>
      <nav className="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.Icon;
          return (
            <button
              key={t.id}
              className={`admin-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </nav>
      <main className="admin-main"><Tab /></main>
    </div>
  );
}
